
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import ModernButton from '@/components/dashboard/ModernButton';
import { 
  Building, 
  Bot, 
  MessageSquare, 
  ChevronRight, 
  Zap, 
  TrendingUp,
  CreditCard,
  BarChart3,
  Heart
} from 'lucide-react';

import { Link } from 'react-router-dom';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { ModernStatCard } from '@/components/ui/modern-stat-card';
import ModernTabNavigation from '@/components/dashboard/ModernTabNavigation';
import { ModernDropdown } from '@/components/ui/modern-dropdown';
import Magic from '@/components/icons/library/Magic';
import Bubbles from '@/components/icons/library/Bubbles';
import Users from '@/components/icons/library/Users';
import ColumnChart from '@/components/icons/library/ColumnChart';
import { Icon } from '@/components/icons';
import { useDashboard, SuperAdminDashboardData, GrowthDataSections, GrowthDatum, RevenueDatum, RevenueValue } from '@/hooks/useDashboard';

const fallbackDashboardData: SuperAdminDashboardData = {
  growthData: {
    weekly: [
      { label: 'W1', businesses: 0, users: 0 },
      { label: 'W2', businesses: 0, users: 0 },
      { label: 'W3', businesses: 0, users: 0 },
      { label: 'W4', businesses: 0, users: 0 },
    ],
    monthly: [
      { label: 'Aug', businesses: 0, users: 0 },
      { label: 'Sep', businesses: 0, users: 0 },
      { label: 'Oct', businesses: 0, users: 0 },
      { label: 'Nov', businesses: 0, users: 0 },
      { label: 'Dec', businesses: 0, users: 0 },
      { label: 'Jan', businesses: 0, users: 0 },
      { label: 'Feb', businesses: 0, users: 0 },
    ],
    yearly: [
      { label: '2021', businesses: 0, users: 0 },
      { label: '2022', businesses: 0, users: 0 },
      { label: '2023', businesses: 0, users: 0 },
      { label: '2024', businesses: 0, users: 0 },
      { label: '2025', businesses: 0, users: 0 },
    ],
  },
  conversationData: [
    { week: 'W1', conversations: 0 },
    { week: 'W2', conversations: 0 },
    { week: 'W3', conversations: 0 },
    { week: 'W4', conversations: 0 },
    { week: 'W5', conversations: 0 },
    { week: 'W6', conversations: 0 },
    { week: 'W7', conversations: 0 },
    { week: 'W8', conversations: 0 },
  ],
  revenueData: [
    { month: 'Jan', revenue: 0, subscriptions: 0, add_on: 0, topups: 0 },
    { month: 'Feb', revenue: 0, subscriptions: 0, add_on: 0, topups: 0 },
    { month: 'Mar', revenue: 0, subscriptions: 0, add_on: 0, topups: 0 },
    { month: 'Apr', revenue: 0, subscriptions: 0, add_on: 0, topups: 0 },
    { month: 'May', revenue: 0, subscriptions: 0, add_on: 0, topups: 0 },
    { month: 'Jun', revenue: 0, subscriptions: 0, add_on: 0, topups: 0 },
    { month: 'Jul', revenue: 0, subscriptions: 0, add_on: 0, topups: 0 },
  ],
  businessTeamMembersData: [
    { month: 'Jan', active: 0, inactive: 0, new: 0 },
    { month: 'Feb', active: 0, inactive: 7, new: 0 },
    { month: 'Mar', active: 0, inactive: 9, new: 0 },
    { month: 'Apr', active: 0, inactive: 6, new: 0 },
    { month: 'May', active: 0, inactive: 8, new: 0 },
    { month: 'Jun', active: 0, inactive: 10, new: 0 },
    { month: 'Jul', active: 0, inactive: 9, new: 0 },
    { month: 'Aug', active: 0, inactive: 7, new: 0 },
  ],
  stats: {
    total_businesses: 0,
    active_agents: 0,
    total_conversations: 0,
    monthly_revenue: 0,
  }
};

type RevenueMetricCategory = 'subscriptions' | 'add_on' | 'topups';
type RevenueMetric = RevenueMetricCategory | 'all';

const revenueMetricKeys: RevenueMetricCategory[] = ['subscriptions', 'add_on', 'topups'];

const revenueMetricLabelMap: Record<RevenueMetric, string> = {
  subscriptions: 'Subscriptions',
  add_on: 'Add-ons',
  topups: 'Top-ups',
  all: 'All Streams',
};

const revenueMetricColorMap: Record<RevenueMetricCategory, string> = {
  subscriptions: 'hsl(var(--primary))',
  add_on: '#b4a7d6',
  topups: '#f6b26b',
};

const normalizeRevenueValue = (value?: RevenueValue): number => {
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value?.parsedValue === 'number' && Number.isFinite(value.parsedValue)) {
    return value.parsedValue;
  }
  if (value?.source !== undefined) {
    const numeric = Number(value.source);
    return Number.isFinite(numeric) ? numeric : 0;
  }
  return 0;
};

const formatRevenueTick = (value: number) => {
  if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toFixed(1)}k`;
  }
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const formatRevenueTotal = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: value < 100 ? 2 : 0
  }).format(value);
};

const SuperAdminDashboard = () => {
  const [growthTab, setGrowthTab] = useState<keyof GrowthDataSections>('monthly');
  const [selectedMetric, setSelectedMetric] = useState<'all' | 'businesses' | 'users'>('all');
  const [businessTeamTab, setBusinessTeamTab] = useState('Today');
  const [businessTeamMetric, setBusinessTeamMetric] = useState('all');
  const [revenueMetric, setRevenueMetric] = useState<RevenueMetric>('subscriptions');
  const { data: dashboardData, isLoading, isError } = useDashboard();

  const growthDataSections: GrowthDataSections = dashboardData?.growthData && Object.keys(dashboardData.growthData).length
    ? dashboardData.growthData
    : fallbackDashboardData.growthData;

  const currentGrowthData: GrowthDatum[] = growthDataSections[growthTab] ?? [];
  const conversationData = dashboardData?.conversationData?.length
    ? dashboardData.conversationData
    : fallbackDashboardData.conversationData;
  const revenueData = dashboardData?.revenueData?.length
    ? dashboardData.revenueData
    : fallbackDashboardData.revenueData;
  const businessTeamMembersData = dashboardData?.businessTeamMembersData?.length
    ? dashboardData.businessTeamMembersData
    : fallbackDashboardData.businessTeamMembersData;

  const revenueChartData = revenueData.map((entry) => {
    const normalizedEntry: { month: string } & Record<RevenueMetricCategory, number> = {
      month: entry.month,
      subscriptions: 0,
      add_on: 0,
      topups: 0,
    };

    normalizedEntry.subscriptions = normalizeRevenueValue(entry.subscriptions);
    normalizedEntry.add_on = normalizeRevenueValue(entry.add_on ?? entry.add_on_agents);
    normalizedEntry.topups = normalizeRevenueValue(entry.topups);

    return normalizedEntry;
  });

  const totalRevenue = revenueData.reduce(
    (sum, entry) => sum + normalizeRevenueValue(entry.revenue),
    0
  );

  const revenueMetricTotal = revenueMetric === 'all'
    ? revenueChartData.reduce((sum, entry) => (
        sum + revenueMetricKeys.reduce((inner, key) => inner + entry[key], 0)
      ), 0)
    : revenueChartData.reduce((sum, entry) => sum + entry[revenueMetric], 0);

  const selectedRevenueLabel = revenueMetricLabelMap[revenueMetric];
  const formattedRevenueTotal = formatRevenueTotal(revenueMetricTotal);


  const platformStats = [
    {
      title: 'Total Businesses',
      value: dashboardData ? dashboardData.stats.total_businesses : 0,
      icon: Users,
      gradient: 'bg-gradient-to-br from-blue-500 to-blue-600'
    },
    {
      title: 'Active Agents',
      value: dashboardData ? dashboardData.stats.active_agents : 0,
      icon: Magic,
      gradient: 'bg-gradient-to-br from-green-500 to-green-600'
    },
    {
      title: 'Total Conversations',
      value: dashboardData ? dashboardData.stats.total_conversations : 0,
      icon: Bubbles,
      gradient: 'bg-gradient-to-br from-purple-500 to-purple-600'
    },
    {
      title: 'Monthly Revenue',
      value: dashboardData ? dashboardData.stats.monthly_revenue : 0,
      icon: ColumnChart,
      gradient: 'bg-gradient-to-br from-orange-500 to-orange-600'
    }
  ];

  const timeTabs = [
    { id: 'weekly', label: 'Weekly' },
    { id: 'monthly', label: 'Monthly' },
    { id: 'yearly', label: 'Yearly' }
  ];

  const metricOptions = [
    { value: 'all', label: 'All Metrics' },
    { value: 'businesses', label: 'Businesses' },
    { value: 'users', label: 'Users' }
  ];

  const businessTeamOptions = [
    { value: 'all', label: 'All Members' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'new', label: 'New' }
  ];

  const revenueMetricOptions: { value: RevenueMetric; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'subscriptions', label: 'Subscriptions' },
    { value: 'add_on', label: 'Add-ons' },
    { value: 'topups', label: 'Top-ups' }
  ];

  // Render chart based on selected metric for platform growth
  const renderPlatformGrowthChart = () => {
    console.log('Rendering platform growth chart with metric:', selectedMetric);
    if (selectedMetric === 'all') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={currentGrowthData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
            <XAxis 
              dataKey="label" 
              tick={{ fontSize: 12, fill: 'currentColor' }}
              className="text-slate-600 dark:text-slate-400"
              axisLine={false}
              tickLine={false}
            />
            <YAxis yAxisId="left" orientation="left" stroke="#0088FE" 
              tick={{ fontSize: 12, fill: 'currentColor' }}
              className="text-slate-600 dark:text-slate-400"
              axisLine={false}
              tickLine={false}
            />
            <YAxis yAxisId="right" orientation="right" stroke="#00C49F" 
              tick={{ fontSize: 12, fill: 'currentColor' }}
              className="text-slate-600 dark:text-slate-400"
              axisLine={false}
              tickLine={false}
            />
            <Tooltip 
               contentStyle={{ 
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '12px',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                color: 'hsl(var(--foreground))',
                fontSize: 12
              }}
            />
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="users" 
              stroke="#f6b26b" 
              strokeWidth={2} 
              dot={false}
              name="Users"
            />
            <Line 
              yAxisId="left"
              type="monotone" 
              dataKey="businesses" 
              stroke="hsl(var(--primary))" 
              strokeWidth={2} 
              dot={false}
              name="Businesses"
            />
          </LineChart>
        </ResponsiveContainer>
      );
    } else {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={currentGrowthData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <defs>
              <linearGradient id="metricGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={ selectedMetric === 'businesses' ? 'hsl(var(--primary))' : '#f6b26b'} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={ selectedMetric === 'businesses' ? 'hsl(var(--primary))' : '#f6b26b'} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
            <XAxis 
              dataKey="label" 
              tick={{ fontSize: 12, fill: 'currentColor' }}
              className="text-slate-600 dark:text-slate-400"
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              tick={{ fontSize: 12, fill: 'currentColor' }}
              className="text-slate-600 dark:text-slate-400"
              axisLine={false}
              tickLine={false}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '12px',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                color: 'hsl(var(--foreground))',
                fontSize: 12
              }}
            />
            <Area 
              type="monotone" 
              dataKey={selectedMetric}
              stroke={selectedMetric === 'businesses' ? 'hsl(var(--primary))' : '#f6b26b'}
              fillOpacity={1} 
              fill="url(#metricGradient)"
              strokeWidth={2}
              dot={false}
              name={selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)}
            />
          </AreaChart>
        </ResponsiveContainer>
      );
    }
  };

  // Render chart for business team members based on selected metric
  const renderRevenueLines = () => {
    if (revenueMetric === 'all') {
      return revenueMetricKeys.map((key) => (
        <Line
          key={key}
          type="monotone"
          dataKey={key}
          stroke={revenueMetricColorMap[key]}
          strokeWidth={2}
          dot={false}
          name={revenueMetricLabelMap[key]}
        />
      ));
    }

    return (
      <Line
        type="monotone"
        dataKey={revenueMetric}
        stroke={revenueMetricColorMap[revenueMetric]}
        strokeWidth={2}
        dot={false}
        name={revenueMetricLabelMap[revenueMetric]}
      />
    );
  };


  return (
    <div className="min-h-screen bg-neutral-100/50 dark:bg-[hsla(0,0%,0%,0.95)]">
      <div className="container max-w-7xl mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">
              Platform Dashboard
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400 mt-1">
              Overview of your entire platform
            </p>
          </div>
        </div>

        {isLoading && !dashboardData && (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-neutral-900/40 p-4 text-sm text-slate-600 dark:text-slate-300">
            Loading live dashboard metrics...
          </div>
        )}

        {isError && (
          <div className="rounded-2xl border border-amber-200 dark:border-amber-500/40 bg-amber-50/80 dark:bg-amber-500/10 p-4 text-sm text-amber-900 dark:text-amber-100">
            Unable to load live data. Showing fallback metrics instead.
          </div>
        )}

        {/* Modern Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {platformStats.map((stat, index) => (
            <div key={index} className="relative group">
              <ModernStatCard
                title={stat.title}
                value={stat.value}
                icon={stat.icon}
                gradient={stat.gradient}
              />
              {/* Quick action overlays */}
              {stat.title === 'Total Businesses' && (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors rounded-2xl flex items-end justify-end p-4 opacity-0 group-hover:opacity-100">
                  <ModernButton variant="ghost" size="sm" asChild className="text-xs">
                    <Link to="/businesses" className="flex items-center">
                      View all <ChevronRight className="ml-1 h-3 w-3" />
                    </Link>
                  </ModernButton>
                </div>
              )}
              {stat.title === 'Monthly Revenue' && (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors rounded-2xl flex items-end justify-end p-4 opacity-0 group-hover:opacity-100">
                  <ModernButton variant="ghost" size="sm" asChild className="text-xs">
                    <Link to="/settings/platform/billing" className="flex items-center">
                      View billing <ChevronRight className="ml-1 h-3 w-3" />
                    </Link>
                  </ModernButton>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Platform Growth - Full Width with Admin Style Controls */}
        <Card className="bg-white dark:bg-neutral-800/60 border-0 rounded-2xl shadow-none overflow-hidden h-full pl-0">
          <CardHeader className="pb-4 pl-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-900 dark:text-slate-100 pl-4">
                Platform Growth
              </CardTitle>
              <div className="flex items-center gap-3">
                <ModernTabNavigation 
                  tabs={timeTabs}
                  activeTab={growthTab}
                  onTabChange={(tab) => setGrowthTab(tab as keyof GrowthDataSections)}
                  className="text-xs"
                />
                <ModernDropdown
                  value={selectedMetric}
                  onValueChange={(value) => setSelectedMetric(value as 'all' | 'businesses' | 'users')}
                  options={metricOptions}
                  placeholder="Select Metric"
                  className="w-32 h-8 text-xs rounded-xl border-slate-200 dark:border-slate-700"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex-1 pl-0 pb-0">
            <div className="h-80">
              {renderPlatformGrowthChart()}
            </div>
          </CardContent>
        </Card>

        {/* Analytics Charts - Admin Style */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Conversation Volume - Admin Style with Bar Chart Icon */}
          <Card className="bg-white dark:bg-neutral-800/60 border-0 rounded-2xl overflow-hidden h-full">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="w-full text-lg font-semibold text-foreground flex items-center justify-between flex-row-reverse gap-2 pl-0">
                  <Icon type='plain' name='Bubbles' className='h-5 w-5 text-muted-foreground' color='hsl(var(--foreground))' />
                  Conversation Volume
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="flex-1">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={conversationData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <defs>
                      <linearGradient id="conversationGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8884d8" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
                    <XAxis 
                      dataKey="week" 
                      tick={{ fontSize: 12, fill: 'currentColor' }}
                      className="text-slate-600 dark:text-slate-400"
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: 'currentColor' }}
                      className="text-slate-600 dark:text-slate-400"
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip 
                     contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '12px',
                      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                      color: 'hsl(var(--foreground))',
                      fontSize: '12px'
                    }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="conversations" 
                      stroke="#b4a7d6" 
                      fill="url(#conversationGradient)" 
                      fillOpacity={1} 
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          
          {/* Revenue Trends - Admin Style with Heart Icon */}
          <Card className="bg-white dark:bg-neutral-800/60 border-0 rounded-2xl overflow-hidden h-full">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2 pl-0">
                  Revenue Trends
                </CardTitle>
                <div className="flex items-center gap-3">
                  <span className="text-xs sm:text-sm text-muted-foreground">
                    <span className="ml-2 font-semibold text-foreground">{formattedRevenueTotal}</span>
                  </span>
                  <ModernDropdown
                    value={revenueMetric}
                    onValueChange={(value) => setRevenueMetric(value as RevenueMetric)}
                    options={revenueMetricOptions}
                    className="w-32 h-8 text-xs rounded-xl border-slate-200 dark:border-slate-700"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-1">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={revenueChartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fontSize: 12, fill: 'currentColor' }}
                      className="text-slate-600 dark:text-slate-400"
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: 'currentColor' }}
                      className="text-slate-600 dark:text-slate-400"
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={formatRevenueTick}
                    />
                    <Tooltip 
                      formatter={(value: number, _name: string | number, entry?: { dataKey?: string | number }) => {
                        const metricKey: RevenueMetric = revenueMetric === 'all'
                          ? (entry?.dataKey as RevenueMetricCategory)
                          : revenueMetric;
                        const resolvedLabel = revenueMetricLabelMap[metricKey] ?? revenueMetricLabelMap[revenueMetric];

                        return [
                          `EUR ${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
                          resolvedLabel
                        ];
                      }}
                      contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '12px',
                      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                      color: 'hsl(var(--foreground))',
                      fontSize: '12px'
                    }}
                    />
                    {renderRevenueLines()}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

      </div>
    </div>
  );
};

export default SuperAdminDashboard;
